import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Link, SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { Caption, H4, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, MouseEventHandler, useMemo } from 'react';

import { CoverLotsComponent } from '@/components/CoverLots/CoverLotsComponent';
import { Flag } from '@/components/Flag/Flag';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogAbsenteeBidders,
    getCatalogCountry,
    getCatalogHasVideo,
    getCatalogIsCatalogOnly,
    getCatalogIsDone,
    getCatalogIsSEOExcluded,
    getCatalogIsTimed,
    getCatalogIsTimedPlus,
    getCatalogIsTimedPlusClosing,
    getCatalogLotsListed,
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogShortAddress,
    getCatalogStartTime,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogBidders, getLiveCatalogStatus } from '@/redux/modules/console';
import {
    AuctionCardClickedTrigger,
    isAuctionCardClickedTrigger,
    useTrackAuctionCardClickedAnalytics,
} from '@/utils/amplitude/analytics/catalog/trackAuctionCardClicked.analytics';
import { AuctionType } from '@/utils/amplitude/analytics/core.types';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getCatalogUrl, getSellerUrl } from '@/utils/urls';

import CatalogDate from '../CatalogDate/CatalogDate';
import ConsoleSocialBadge from '../ConsoleSocial/ConsoleSocialBadge';
import { CatalogCardProgressBar } from './CatalogCardProgressBar';
import BidNowLink from './components/BidNowLink/BidNowLink';
import { CatalogCardDate } from './components/CatalogCardDate/CatalogCardDate';

type Props = {
    catalogId: number;
    className?: string;
    featured?: boolean;
    lazyLoadImages?: boolean;
    position?: number;
};

const CatalogCardComponent = ({ catalogId, className = 'catalog-card', featured, lazyLoadImages, position }: Props) => {
    // Standard catalog data selectors
    const isSEOExcluded = useAppSelector((state) => getCatalogIsSEOExcluded(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const name = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const isDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const showLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const isTimed = useAppSelector((state) => getCatalogIsTimed(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const isTimedPlusClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const lotCount = useAppSelector((state) => getCatalogLotsListed(state, catalogId));
    const countryCode = useAppSelector((state) => getCatalogCountry(state, catalogId));
    const location = useAppSelector((state) => getCatalogShortAddress(state, catalogId));

    // Live catalog data selectors
    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));

    const formatMessage = useAppFormatMessage();
    const live = showLive || liveStatus === 'live';
    const viewers = bidders + absenteeBidders;

    const trackAuctionCardClicked = useTrackAuctionCardClickedAnalytics();

    const auctionType = useMemo((): AuctionType => {
        if (isTimedPlus) {
            return AuctionType.TimedPlus;
        }
        if (isCatalogOnly) {
            return AuctionType.PreviewOnly;
        }
        if (isTimed) {
            return AuctionType.Timed;
        }
        return AuctionType.Live;
    }, [isTimedPlus, isCatalogOnly, isTimed]);

    const handleCardClick: MouseEventHandler<HTMLDivElement> = (event) => {
        if (position === undefined || position === null) {
            return;
        }

        const target = event.target as HTMLElement;
        const triggerElement = target.closest('[data-track-trigger]') as HTMLElement | null;
        if (!triggerElement) {
            return;
        }

        const rawTrigger = triggerElement.dataset.trackTrigger;
        if (!rawTrigger || !isAuctionCardClickedTrigger(rawTrigger)) {
            return;
        }

        trackAuctionCardClicked({
            auctionType,
            catalogId,
            houseId: sellerId,
            lotCount,
            position,
            trigger: rawTrigger,
        });
    };

    const catalogUrl = getCatalogUrl(catalogId, title);
    const rel = isSEOExcluded ? 'nofollow' : '';

    return (
        <div
            className={cn('relative block', className)}
            onClick={handleCardClick}
            suppressHydrationWarning
        >
            <CoverLotsComponent
                catalogId={catalogId}
                className="block"
                featured={featured}
                isSEOExcluded={isSEOExcluded}
                lazyLoadImages={lazyLoadImages}
                title={title}
            />
            {isTimedPlus && !isDone && isTimedPlusClosing ? (
                <Badge
                    className="absolute top-xs left-xs"
                    color="critical"
                    emphasis="bold"
                    size="sm"
                >
                    <LabelStrong className="text-text-inverse uppercase">{formatMessage(['closing'])}</LabelStrong>
                </Badge>
            ) : live && !isTimedPlus ? (
                <ConsoleSocialBadge
                    className="absolute top-xs left-xs"
                    hasVideo={hasVideo}
                    viewers={viewers}
                />
            ) : null}
            <div className="relative flex flex-col border border-t-0 border-border-secondary p-xs">
                {((live && !isTimedPlus) || (isTimedPlusClosing && isTimedPlus)) && (
                    <CatalogCardProgressBar
                        catalogId={catalogId}
                        className="absolute inset-x-0 top-[-4px] z-[2]"
                    />
                )}
                <H4
                    className={cn('h-md truncate text-left', {
                        'max-w-full': !featured,
                        'max-w-[calc(100%-130px)] xsMax:max-w-full': featured,
                    })}
                >
                    <SubtleMonochromeLink
                        data-testid="featuredAuctionTitle"
                        data-track-trigger={AuctionCardClickedTrigger.TitleClick}
                        href={catalogUrl}
                        rel={rel}
                    >
                        {title}
                    </SubtleMonochromeLink>
                </H4>
                <div
                    className={cn('flex h-md items-center justify-start', {
                        'max-w-full': !featured,
                        'max-w-[calc(100%-130px)] xsMax:max-w-full': featured,
                    })}
                >
                    <SubtleMonochromeLink
                        className="overflow-hidden text-ellipsis whitespace-nowrap"
                        data-track-trigger={AuctionCardClickedTrigger.HouseNameClick}
                        href={getSellerUrl(sellerId, name)}
                        size="xs"
                    >
                        {name}
                    </SubtleMonochromeLink>
                </div>
                <Caption
                    className={cn('flex h-md items-center', {
                        'justify-between': featured,
                        'justify-start': !featured,
                    })}
                    color="secondary"
                    suppressHydrationWarning
                >
                    <CatalogCardDate catalogId={catalogId} />
                    {featured && (
                        <Link href={catalogUrl}>
                            <Button
                                className="max-[881px]:hidden absolute top-xs right-xs inline-flex"
                                data-testid="exploreButton"
                            >
                                {formatMessage(['explore'])}
                            </Button>
                        </Link>
                    )}
                </Caption>
                {!featured && (
                    <div className="flex h-md items-center justify-between">
                        <div className="-ml-3xs flex max-w-[calc(100%-64px)] flex-1 items-center justify-start">
                            {countryCode && <Flag countryCode={countryCode} />}
                            <Caption
                                className="ml-2xs max-w-[calc(100%-50px)] overflow-hidden text-ellipsis whitespace-nowrap"
                                color="secondary"
                            >
                                {location}
                            </Caption>
                        </div>
                        {live ? (
                            <div data-track-trigger={AuctionCardClickedTrigger.BidNowClick}>
                                <BidNowLink catalogId={catalogId} />
                            </div>
                        ) : (
                            <>
                                {!isTimedPlus && (
                                    <CatalogDate
                                        date={saleStartTs}
                                        onlyShowCountdown
                                    />
                                )}
                                {isDone && <Caption color="secondary">{formatMessage(['auction_ended'])}</Caption>}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export const CatalogCard = memo<Props>(CatalogCardComponent);
